import { Options } from '@angular-slider/ngx-slider';
import { KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MultiImageSelectDialogComponent } from '../multi-image-select-dialog/multi-image-select-dialog.component';

@Component({
  selector: 'app-gui',
  templateUrl: './gui.component.html',
  styleUrls: ['./gui.component.scss']
})
export class GuiComponent implements OnInit, OnDestroy {

  lang = 'de';
  @Input() jsongui: any;
  @Input() projectdata: any;

  @Input() celllabels: any = true;

  focusedColorPicker: string | null = null;
  isColorPickerOpen = false;

  @Output() conrolChanged = new EventEmitter<any>();
  // public keepOriginalOrder = (a, b) :any=> {
  //   a.key as any
  // };

  //vgl https://stackoverflow.com/questions/52793944/angular-keyvalue-pipe-sort-properties-iterate-in-order
  keepOriginalOrder = (a: KeyValue<number, any>, b: KeyValue<number, any>): number => {
    return 0;
  }

  // Parse cell value format "URL(value);URL(value)" and convert to selectOptions
  parseCellValueToSelectOptions(cellValue: string): any[] {
    if (!cellValue || typeof cellValue !== 'string') return [];

    const items = cellValue.split(';').filter(item => item.trim());

    return items.map(item => {
      const trimmedItem = item.trim();

      // Look for the pattern: anything followed by (value) at the end
      const match = trimmedItem.match(/^(.+?)\(([^)]*)\)$/);

      if (match) {
        // match[1] is URL, match[2] is value in parentheses
        const url = match[1];
        const value = match[2];

        return {
          name: url,
          wert: value
        };
      }

      // Format: just "URL" or malformed - return as URL with empty value
      return {
        name: trimmedItem,
        wert: ''
      };
    });
  }

  // Check if item needs selectOptions parsing from cell value
  ensureSelectOptionsFromCellValue(item: any): void {
    // Add guard to prevent infinite loops
    if (item.value._parsing) return;

    // Use cellValue as priority source, then outputValue as fallback
    const sourceValue = item.value.cellValue || item.value.outputValue;

    // If item has a source value that looks like tuple format, parse it
    if (sourceValue && typeof sourceValue === 'string') {
      // Check if the value contains tuple format: URL(value) or multiple URLs separated by ;
      const hasTupleFormat = sourceValue.includes('(') && sourceValue.includes(')');
      const hasMultipleUrls = sourceValue.includes(';');

      if (hasTupleFormat || (hasMultipleUrls && this.isImageUrl(sourceValue.split(';')[0]))) {
        // Set parsing flag to prevent recursion
        item.value._parsing = true;

        const parsedOptions = this.parseCellValueToSelectOptions(sourceValue);

        if (parsedOptions.length > 0) {
          // Store current outputValue before overwriting
          const currentOutputValue = item.value.outputValue;

          item.value.selectOptions = parsedOptions;

          // Set outputValue to the first option's value if no current valid selection
          const matchingOption = parsedOptions.find(opt => opt.wert === currentOutputValue);
          if (matchingOption) {
            item.value.outputValue = currentOutputValue; // Keep current selection
          } else {
            item.value.outputValue = parsedOptions[0].wert || ''; // Set to first option's value
          }
        }

        // Clear parsing flag
        item.value._parsing = false;
      }
    }
  }

  cpToggleChange(event: any) {
    console.log('cpToggleChange', event);
    this.isColorPickerOpen = event;
  }

  pickerclose(test, event: any) {
    console.log('pickerclose', event);
    this.isColorPickerOpen = false;
    this.focusedColorPicker = null;
  }

  // Close color picker when clicking on the GUI panel
  closeColorPicker() {
    if (this.isColorPickerOpen || this.focusedColorPicker) {
      this.isColorPickerOpen = false;
      this.focusedColorPicker = null;
    }
  }

  slideroptions: any;
  valueSubject = new BehaviorSubject<number>(0);

  // Predefined color palette - comprehensive set of commonly used colors
  predefinedColors = [
    // Basic colors
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    // Grayscale
    '#808080', '#C0C0C0', '#404040', '#202020',
    // Reds
    '#8B0000', '#DC143C', '#B22222', '#CD5C5C', '#F08080', '#FA8072', '#E9967A', '#FFA07A',
    // Oranges
    '#FF4500', '#FF6347', '#FF7F50', '#FF8C00', '#FFA500', '#FFB347', '#FFCCCB',
    // Yellows
    '#FFD700', '#FFFFE0', '#FFFACD', '#FFEFD5', '#FFEBCD', '#F0E68C', '#BDB76B',
    // Greens
    '#006400', '#008000', '#228B22', '#32CD32', '#7CFC00', '#ADFF2F', '#9AFF9A', '#98FB98',
    '#90EE90', '#00FF7F', '#00FA9A', '#40E0D0', '#48D1CC', '#00CED1',
    // Blues
    '#000080', '#0000CD', '#4169E1', '#6495ED', '#87CEEB', '#87CEFA', '#00BFFF', '#1E90FF',
    '#6A5ACD', '#7B68EE', '#9370DB', '#8A2BE2', '#9400D3', '#9932CC',
    // Purples and magentas
    '#8B008B', '#800080', '#DA70D6', '#DDA0DD', '#EE82EE', '#FF69B4', '#FF1493', '#DC143C',
    // Browns
    '#8B4513', '#A0522D', '#CD853F', '#D2B48C', '#F4A460', '#DEB887', '#D2691E', '#BC8F8F',
    // Material Design inspired
    '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
    '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
    // Additional professional colors
    '#607D8B', '#795548', '#9E9E9E', '#546E7A', '#37474F', '#263238'
  ];

  constructor(private cdr: ChangeDetectorRef, private dialog: MatDialog) {
    const newOptions: Options = Object.assign({}, this.slideroptions);
    newOptions.ceil = 100;
    this.slideroptions = newOptions;
  }

  ngOnInit(): void {
    this.valueSubject.pipe(debounceTime(5)).subscribe(value => {
      // http request can go here
      //  console.log('debounce', JSON.stringify(value));
      this.conrolChanged.emit(value);
    });

    // Close image and color dropdowns when clicking outside
    this.clickOutsideListener = (event: Event) => {
      const target = event.target as HTMLElement;
      const isInsideImageSelect = target.closest('.image-select-wrapper');
      const isInsideColorSelect = target.closest('.color-select-wrapper');
      if (!isInsideImageSelect) {
        this.openImageDropdowns.clear();
      }
      if (!isInsideColorSelect) {
        this.openColorDropdowns.clear();
      }
    };
    document.addEventListener('click', this.clickOutsideListener);
  }
  // onSlide(event: MatSliderChange) {
  // }

  sliderChange(event, item) {
    item.value.outputValue = event.srcElement.value;
    console.log('onchanc', event);
    this.valueSubject.next(item.value);
    if (event) {
      // item.value = event.value;

    }
    //    item.value.outputValue = event;
  }

  radiochange(event, item) {
    console.log('radiochange', event, item.value);
    //    this.controlC(item.value);
  }
  controlC(value: any) {
    if (value.typ != "COLORCHOOSER")
      this.closeColorPicker();

    if (value?.outputValue === 'exportScene') {
      this.conrolChanged.emit({ action: 'exportScene' });
    } else {
      // Add source context for better handling
      this.conrolChanged.emit({
        ...value,
        source: 'gui'
      });
    }
  }
  controlUp(item: any) {
    item.outputValue++;
    this.controlC(item);
  }
  controlDown(item: any) {
    item.outputValue--;
    this.controlC(item);

  }
  focusControl(controlName: any) {
    this.focusedColorPicker = controlName;
    this.isColorPickerOpen = true;
  }

  blurControl() {
    // Don't close immediately to allow clicking on the color picker
    setTimeout(() => {
      if (!this.isColorPickerOpen) {
        this.focusedColorPicker = null;
      }
    }, 200);
  }

  nbrChange(event, item) {
    console.log('nbrChange', event);
    item.value.outputValue = event;
    this.cdr.detectChanges();
    this.controlC(item.value);
  }

  // Check if a value is an image URL
  isImageUrl(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }

    // Check if value contains URLs (Firebase Storage URLs or general image URLs)
    // Note: URLs are separated by semicolons in this system
    const firebaseUrlPattern = /https:\/\/firebasestorage\.googleapis\.com\/[^\s;]+/g;
    const generalUrlPattern = /https?:\/\/[^\s;]+\.(png|jpg|jpeg|gif|svg|webp|PNG|JPG|JPEG|GIF|SVG|WEBP)/g;

    const firebaseUrls = value.match(firebaseUrlPattern);
    const generalUrls = value.match(generalUrlPattern);

    const isImage = (firebaseUrls && firebaseUrls.length > 0) || (generalUrls && generalUrls.length > 0);

    // Debug log only for radio-related checks or when explicitly requested
    if (value.includes('http') && (value.includes('firebase') || value.includes('.png') || value.includes('.jpg'))) {
      console.log('🖼️ [isImageUrl] Image URL check:', {
        value: value,
        firebaseUrls: firebaseUrls,
        generalUrls: generalUrls,
        isImage: isImage
      });
    }

    return isImage;
  }

  // Extract filename from URL for display
  getFilenameFromUrl(url: string): string {
    if (!url) return '';

    // Handle semicolon-separated URLs - show first one with count if multiple
    const urls = url.split(';').map(u => u.trim()).filter(u => u.length > 0);
    const firstUrl = urls[0];

    if (!firstUrl) return '';

    const urlParts = firstUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    const displayName = filename.split('?')[0]; // Remove query parameters

    // If Firebase URL, decode the filename
    let finalName = displayName;
    if (firstUrl.includes('firebasestorage.googleapis.com')) {
      try {
        const decodedName = decodeURIComponent(displayName);
        // Extract just the actual filename part (after last '/')
        const actualFilename = decodedName.split('%2F').pop() || decodedName;
        finalName = actualFilename;
      } catch (e) {
        finalName = displayName;
      }
    }

    // Add count if multiple URLs
    if (urls.length > 1) {
      return `${finalName} (+${urls.length - 1} more)`;
    }

    return finalName;
  }

  // Ensure selectOptions are ready and return true if they contain images
  initializeAndCheckImageOptions(item: any): boolean {
    // CHECK IF EXISTING selectOptions ARE MALFORMED (both name and wert contain URLs)
    if (item.value.selectOptions && item.value.selectOptions.length > 0) {
      const firstOption = item.value.selectOptions[0];
      if (firstOption.name && firstOption.wert &&
        firstOption.name.includes('(') && firstOption.name.includes(')') &&
        firstOption.name === firstOption.wert) {

        // The selectOptions are malformed - both name and wert contain the full URL(value)
        // We need to reparse them correctly
        const repairedOptions = item.value.selectOptions.map(option => {
          const match = option.name.match(/^(.+?)\(([^)]*)\)$/);
          if (match) {
            return {
              name: match[1],  // URL without parentheses
              wert: match[2]   // Value inside parentheses
            };
          }
          return option; // Fallback if no match
        });

        item.value.selectOptions = repairedOptions;

        // Also fix the outputValue if it's malformed
        if (item.value.outputValue && item.value.outputValue.includes('(') && item.value.outputValue.includes(')')) {
          const match = item.value.outputValue.match(/^(.+?)\(([^)]*)\)$/);
          if (match) {
            item.value.outputValue = match[2]; // Set to value in parentheses
          }
        }
      }
    }

    // If we still don't have selectOptions, try parsing from cellValue/outputValue
    if (!item.value.selectOptions) {
      // Check if we have cell value data that contains image options
      if (item.value.cellValue && typeof item.value.cellValue === 'string') {
        const hasTupleFormat = item.value.cellValue.includes('(') && item.value.cellValue.includes(')');
        const hasMultipleUrls = item.value.cellValue.includes(';');

        if (hasTupleFormat || (hasMultipleUrls && this.isImageUrl(item.value.cellValue.split(';')[0]))) {
          // Parse the cell value to create selectOptions
          item.value.selectOptions = this.parseCellValueToSelectOptions(item.value.cellValue);
        }
      }
      // Also check outputValue as fallback
      else if (item.value.outputValue && typeof item.value.outputValue === 'string') {
        const hasTupleFormat = item.value.outputValue.includes('(') && item.value.outputValue.includes(')');
        const hasMultipleUrls = item.value.outputValue.includes(';');

        if (hasTupleFormat || (hasMultipleUrls && this.isImageUrl(item.value.outputValue.split(';')[0]))) {
          this.ensureSelectOptionsFromCellValue(item);
        }
      }
    }

    const selectOptions = item.value.selectOptions;
    if (!selectOptions || selectOptions.length === 0) return false;
    return selectOptions.some(option => this.isImageUrl(option.name));
  }

  // Check if select options contain any image URLs
  hasImageOptions(selectOptions: any[], item?: any): boolean {
    // Always try to initialize if we have an item but no selectOptions
    if (item && (!selectOptions || selectOptions.length === 0)) {
      this.initializeAndCheckImageOptions(item);
      selectOptions = item.value.selectOptions;
    }

    // Now check if we have selectOptions with image URLs
    if (selectOptions && selectOptions.length > 0) {
      return selectOptions.some(option => this.isImageUrl(option.name));
    }

    return false;
  }

  // Get the first image URL from potentially semicolon-separated URLs
  getFirstImageUrl(url: string): string {
    if (!url) return '';

    const urls = url.split(';').map(u => u.trim()).filter(u => u.length > 0);
    return urls[0] || '';
  }

  // Handle image selection in image gallery
  selectImageOption(item: any, imageUrl: string): void {
    if (!item.value.selectOptions || !Array.isArray(item.value.selectOptions)) {
      console.warn('No selectOptions available for image selection');
      return;
    }

    // Find the selected option by matching the image URL with option.name
    const selectedOption = item.value.selectOptions.find(option => option.name === imageUrl);

    if (selectedOption) {
      // Set outputValue ONLY to the wert (value in brackets) - never use URL as fallback
      item.value.outputValue = selectedOption.wert || '';

      // Trigger change detection and save the value
      this.controlC(item.value);
    } else {
      // Fallback: set to empty and use standard control method
      item.value.outputValue = '';
      this.controlC(item.value);
    }
  }

  // Get the value (wert) for the currently selected image
  getSelectedImageValue(item: any): string {
    // Ensure selectOptions are parsed
    this.initializeAndCheckImageOptions(item);

    if (!item.value.outputValue || !item.value.selectOptions) return '';

    // Find the option where wert matches the outputValue
    const selectedOption = item.value.selectOptions.find(option => option.wert === item.value.outputValue);
    return selectedOption?.wert || item.value.outputValue || '';
  }

  // Get the URL of the currently selected image based on its value
  getSelectedImageUrl(item: any): string {
    // Ensure selectOptions are parsed
    this.initializeAndCheckImageOptions(item);

    if (!item.value.outputValue || !item.value.selectOptions) return '';

    // Find the option where wert matches the outputValue and return its URL
    const selectedOption = item.value.selectOptions.find(option => option.wert === item.value.outputValue);
    return selectedOption?.name || '';
  }

  // Handle image loading errors
  onImageError(event: any): void {
    // Replace with a placeholder or hide the image
    event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA4MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0yNSAyMEw0MCAzNUw1NSAyMEg2MEw0MCA0MEwyMCA2MEgyNVoiIGZpbGw9IiNDQ0NDQ0MiLz4KPHN2Zz4K';
    event.target.alt = 'Image not found';
  }

  // Track which image dropdowns are open
  private openImageDropdowns = new Set<string>();
  private clickOutsideListener?: (event: Event) => void;

  ngOnDestroy(): void {
    // Clean up the click outside listener
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
    }
  }

  // Toggle image dropdown open/closed
  toggleImageDropdown(controlName: string): void {
    if (this.openImageDropdowns.has(controlName)) {
      this.openImageDropdowns.delete(controlName);
    } else {
      // Close all other dropdowns first
      this.openImageDropdowns.clear();
      this.openImageDropdowns.add(controlName);
    }
  }

  // Check if a specific image dropdown is open
  isImageDropdownOpen(controlName: string): boolean {
    return this.openImageDropdowns.has(controlName);
  }

  // Close a specific image dropdown
  closeImageDropdown(controlName: string): void {
    this.openImageDropdowns.delete(controlName);
  }

  openMultiImageSelectDialog(item: any): void {
    // Convert current selectOptions to the expected format for the dialog
    const existingOptions = item.value.selectOptions || [];

    const dialogRef = this.dialog.open(MultiImageSelectDialogComponent, {
      width: '1400px',
      height: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        existingOptions: existingOptions,
        returnFormat: 'selectOptions'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success && result.options) {
        // Merge new options with existing ones, avoiding duplicates by URL
        const existingUrls = new Set(existingOptions.map(opt => opt.name));
        const newOptions = result.options.filter(opt => !existingUrls.has(opt.name));

        // Update selectOptions with merged list
        item.value.selectOptions = [...existingOptions, ...newOptions];

        // Keep current outputValue if it exists in the options, otherwise set to first option's value
        const currentValueExists = item.value.selectOptions.some(opt => opt.wert === item.value.outputValue);
        if (!currentValueExists && item.value.selectOptions.length > 0) {
          item.value.outputValue = item.value.selectOptions[0].wert || '';
        }

        // IMPORTANT: Persist all selectOptions as formatted string to the cell
        const formattedOptions = item.value.selectOptions.map(opt => `${opt.name}(${opt.wert})`).join(';');

        // Store the formatted options in the output value temporarily for the cell update
        const originalOutputValue = item.value.outputValue;
        item.value.outputValue = formattedOptions;

        // Use the standard controlC method to ensure proper sheet context
        this.controlC(item.value);

        // Restore the original output value for display
        item.value.outputValue = originalOutputValue;
      }
    });
  }

  // Color palette methods (similar to image selection pattern)

  // Check if this control has color options (exactly like select component)
  hasColorOptions(item: any): boolean {
    if (!item?.value || item.value.typ !== 'COLORCHOOSER') {
      return false;
    }

    // Check if selectOptions are already populated (from app.worker.ts)
    return item.value.selectOptions && Array.isArray(item.value.selectOptions) && item.value.selectOptions.length > 1;
  }

  // Get color options (following select component pattern)
  getColorOptions(item: any): string[] {
    if (!item?.value || item.value.typ !== 'COLORCHOOSER') {
      return [];
    }

    // Return colors from selectOptions (populated by hasColorOptions)
    return item.value.selectOptions ? item.value.selectOptions.map(opt => opt.name) : [];
  }

  // Select a color option (following select component pattern)
  selectColorOption(item: any, color: string): void {
    if (!item?.value) {
      console.warn('Invalid item for color selection');
      return;
    }

    // Update the outputValue like select component does
    item.value.outputValue = color;

    // Use the same method as select component for consistency
    this.controlC(item.value);
  }

  // Helper method to find control by name
  private findControlByName(controlName: string): any {
    // Traverse through the GUI structure to find the control
    const findInNode = (node: any): any => {
      if (node && typeof node === 'object') {
        if (node.controlName === controlName) {
          console.log('🔍 [findControlByName] Found control:', {
            controlName,
            node,
            typ: node.typ,
            row: node.row,
            value: node.value
          });
          return node;
        }

        if (node.childs && Array.isArray(node.childs)) {
          for (const child of node.childs) {
            const found = findInNode(child);
            if (found) return found;
          }
        }
      }
      return null;
    };

    const result = findInNode(this.jsongui);
    if (!result) {
      console.log('🔍 [findControlByName] Control not found:', controlName);
      console.log('🔍 [findControlByName] Available controls:', this.getAllControlNames());
    }
    return result;
  }

  // Helper method to get all control names for debugging
  private getAllControlNames(): string[] {
    const names: string[] = [];
    const findNames = (node: any): void => {
      if (node && typeof node === 'object') {
        if (node.controlName) {
          names.push(node.controlName);
        }
        if (node.childs && Array.isArray(node.childs)) {
          for (const child of node.childs) {
            findNames(child);
          }
        }
      }
    };
    findNames(this.jsongui);
    return names;
  }

  // Helper method to parse colors from semicolon-separated string
  private parseColorsFromString(colorString: string): string[] {
    if (!colorString || typeof colorString !== 'string') {
      return [];
    }

    return colorString
      .split(';')
      .map(color => color.trim())
      .filter(color => color.length > 0 && this.isValidHexColor(color))
      .map(color => this.normalizeHexColor(color));
  }

  // Check if a string is a valid hex color
  private isValidHexColor(color: string): boolean {
    if (!color || typeof color !== 'string') return false;

    // Remove any whitespace
    color = color.trim();

    // Check for 6-digit hex colors with # prefix
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      return true;
    }

    // Check for 3-digit hex colors with # prefix (convert to 6-digit)
    if (/^#[0-9A-F]{3}$/i.test(color)) {
      return true;
    }

    // Check for hex colors without # prefix (add it automatically)
    if (/^[0-9A-F]{6}$/i.test(color)) {
      return true;
    }

    if (/^[0-9A-F]{3}$/i.test(color)) {
      return true;
    }

    return false;
  }

  // Normalize hex color to #RRGGBB format
  private normalizeHexColor(color: string): string {
    if (!color || typeof color !== 'string') return '';

    // Remove any whitespace
    color = color.trim().toUpperCase();

    // Already in correct format
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      return color;
    }

    // Convert 3-digit hex to 6-digit
    if (/^#[0-9A-F]{3}$/i.test(color)) {
      const hex = color.slice(1);
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }

    // Add # prefix to 6-digit hex
    if (/^[0-9A-F]{6}$/i.test(color)) {
      return `#${color}`;
    }

    // Add # prefix and convert 3-digit to 6-digit
    if (/^[0-9A-F]{3}$/i.test(color)) {
      return `#${color[0]}${color[0]}${color[1]}${color[1]}${color[2]}${color[2]}`;
    }

    return color; // Return as-is if no pattern matches
  }

  // Get the currently selected color
  getSelectedColor(item: any): string {
    if (!item.value.outputValue) return '';

    // For single color selection, just return the outputValue if it's a valid color
    if (this.isValidHexColor(item.value.outputValue)) {
      return item.value.outputValue;
    }

    return '';
  }

  // Track which color dropdowns are open
  private openColorDropdowns = new Set<string>();

  // Toggle color dropdown open/closed
  toggleColorDropdown(controlName: string): void {
    if (this.openColorDropdowns.has(controlName)) {
      this.openColorDropdowns.delete(controlName);
    } else {
      // Close all other dropdowns first
      this.openColorDropdowns.clear();
      this.openColorDropdowns.add(controlName);
    }
  }

  // Check if a specific color dropdown is open
  isColorDropdownOpen(controlName: string): boolean {
    return this.openColorDropdowns.has(controlName);
  }

  // Close a specific color dropdown
  closeColorDropdown(controlName: string): void {
    this.openColorDropdowns.delete(controlName);
  }

  // TrackBy function for image options to prevent unnecessary re-rendering
  trackImageOption(index: number, option: any): string {
    return option.name || option.wert || index.toString();
  }

  // TrackBy function for color options to prevent unnecessary re-rendering  
  trackColorOption(index: number, color: string): string {
    return color;
  }

  // Cache for loaded images to prevent re-loading
  private imageLoadCache = new Set<string>();

  // Optimized image loading with caching
  onImageLoad(imageUrl: string): void {
    this.imageLoadCache.add(imageUrl);
  }

  // Check if image is already cached
  isImageCached(imageUrl: string): boolean {
    return this.imageLoadCache.has(imageUrl);
  }

  // Radio Button Image Methods (similar to Select component)

  // Check if radio button has image options from selectOptions or cell value
  hasRadioImageOptions(item: any): boolean {
    if (!item?.value || item.value.typ !== 'RADIO') {
      return false;
    }

    // Only log once per radio button to avoid spam
    if (!item.value._debugLogged) {
      console.log('🔍 [hasRadioImageOptions] Checking radio button:', {
        controlName: item.value.controlName,
        cell: item.value.cell,
        hasSelectOptions: !!item.value.selectOptions,
        selectOptionsLength: item.value.selectOptions?.length || 0
      });
      item.value._debugLogged = true;
    }

    // Use the same logic as hasImageOptions for consistency
    let selectOptions = item.value.selectOptions;

    // Always try to initialize if we have an item but no selectOptions
    if (!selectOptions || selectOptions.length === 0) {
      this.initializeAndCheckImageOptions(item);
      selectOptions = item.value.selectOptions;
    }

    // Now check if we have selectOptions with image URLs
    if (selectOptions && selectOptions.length > 0) {
      const hasImages = selectOptions.some(option => this.isImageUrl(option.name));

      if (hasImages && !item.value._resultLogged) {
        console.log('🔍 [hasRadioImageOptions] ✅ Found image options:', {
          controlName: item.value.controlName,
          selectOptionsCount: selectOptions.length,
          firstOptionName: selectOptions[0]?.name
        });
        item.value._resultLogged = true;
      }

      return hasImages;
    }

    return false;
  }

  // Get image options for radio buttons
  getRadioImageOptions(item: any): any[] {
    if (!item?.value || item.value.typ !== 'RADIO') {
      return [];
    }

    // Ensure selectOptions are parsed
    this.initializeAndCheckImageOptions(item);

    return item.value.selectOptions || [];
  }

  // Select radio image option (same as select component)
  selectRadioImageOption(item: any, imageUrl: string, value: string): void {
    if (!item?.value) {
      console.warn('Invalid item for radio image selection');
      return;
    }

    // Ensure we have valid selectOptions
    if (!item.value.selectOptions || !Array.isArray(item.value.selectOptions)) {
      console.warn('No selectOptions available for radio selection');
      return;
    }

    // Find the selected option by matching the image URL with option.name (same as select component)
    const selectedOption = item.value.selectOptions.find(option => option.name === imageUrl);

    if (selectedOption) {
      // Set outputValue ONLY to the wert (value in brackets) - same as select component
      item.value.outputValue = selectedOption.wert || '';

      console.log('🔘 [Radio Image Selection]', {
        controlName: item.value.controlName,
        newValue: item.value.outputValue,
        selectedOption: selectedOption,
        sheetInfo: item.value.sheet
      });

      // Trigger change detection and save the value (same as select component)
      this.controlC(item.value);
    } else {
      // Fallback: set to empty and use standard control method (same as select component)
      item.value.outputValue = '';
      this.controlC(item.value);
    }
  }

  // Select radio text option (for traditional radio buttons)
  selectRadioTextOption(item: any, value: string): void {
    if (!item?.value) {
      console.warn('Invalid item for radio text selection');
      return;
    }

    const previousValue = item.value.outputValue;
    item.value.outputValue = value;

    console.log('🔘 [Radio Text Selection]', {
      controlName: item.value.controlName,
      previousValue: previousValue,
      newValue: item.value.outputValue
    });

    // Trigger change detection and save the value
    this.controlC(item.value);

    // Force change detection to update UI immediately
    this.cdr.detectChanges();
  }

  // Get the currently selected radio image URL
  getSelectedRadioImageUrl(item: any): string {
    if (!item?.value || !item.value.selectOptions) {
      return '';
    }

    // Find the option where wert matches the outputValue and return its URL
    const selectedOption = item.value.selectOptions.find(option => option.wert === item.value.outputValue);
    return selectedOption?.name || '';
  }

  // Get the currently selected radio image value 
  getSelectedRadioImageValue(item: any): string {
    if (!item?.value) {
      return '';
    }

    return item.value.outputValue || '';
  }

  // Check if radio button is selected for a specific value
  isRadioSelected(item: any, value: string): boolean {
    return item?.value?.outputValue === value;
  }

  // Open multi-image select dialog for radio buttons
  openRadioMultiImageSelectDialog(item: any): void {
    // Convert current selectOptions to the expected format for the dialog
    const existingOptions = item.value.selectOptions || [];

    const dialogRef = this.dialog.open(MultiImageSelectDialogComponent, {
      width: '1400px',
      height: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        existingOptions: existingOptions,
        returnFormat: 'selectOptions'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success && result.options) {
        // Merge new options with existing ones, avoiding duplicates by URL
        const existingUrls = new Set(existingOptions.map(opt => opt.name));
        const newOptions = result.options.filter(opt => !existingUrls.has(opt.name));

        // Update selectOptions with merged list
        item.value.selectOptions = [...existingOptions, ...newOptions];

        // Keep current outputValue if it exists in the options, otherwise set to first option's value
        const currentValueExists = item.value.selectOptions.some(opt => opt.wert === item.value.outputValue);
        if (!currentValueExists && item.value.selectOptions.length > 0) {
          item.value.outputValue = item.value.selectOptions[0].wert || '';
        }

        // IMPORTANT: Persist all selectOptions as formatted string to the cell
        const formattedOptions = item.value.selectOptions.map(opt => `${opt.name}(${opt.wert})`).join(';');

        // Store the formatted options in the output value temporarily for the cell update
        const originalOutputValue = item.value.outputValue;
        item.value.outputValue = formattedOptions;

        // Use the standard controlC method to ensure proper sheet context
        this.controlC(item.value);

        // Restore the original output value for display
        item.value.outputValue = originalOutputValue;
      }
    });
  }

  // Get radio button group name (important for radio button behavior)
  getRadioGroupName(item: any): string {
    // Use the control name or generate a unique group name
    return item?.value?.controlName ? `radio_group_${item.value.controlName}` : 'radio_group_default';
  }

  // Debug method to understand radio button structure
  debugRadioButton(item: any): void {
    console.log('🔬 [DEBUG RADIO BUTTON] Full structure:', {
      item: item,
      value: item?.value,
      typ: item?.value?.typ,
      controlName: item?.value?.controlName,
      cell: item?.value?.cell,
      selectOptions: item?.value?.selectOptions,
      cellValue: item?.value?.cellValue,
      cellValueType: typeof item?.value?.cellValue,
      cellValueLength: item?.value?.cellValue?.length,
      outputValue: item?.value?.outputValue,
      outputValueType: typeof item?.value?.outputValue,
      outputValueLength: item?.value?.outputValue?.length,
      allProperties: Object.keys(item?.value || {})
    });

    // Debug the sheet structure to understand how to access parameter cells
    console.log('🔬 [DEBUG RADIO BUTTON] Sheet data access attempt:', {
      hasSheet: !!item?.value?.sheet,
      sheet: item?.value?.sheet,
      projectdata: this.projectdata,
      hasProjectdata: !!this.projectdata,
      jsongui: this.jsongui,
      hasJsongui: !!this.jsongui
    });

    // Try to access parameter cell using same logic as select components
    if (item?.value?.cell && item?.value?.sheet) {
      const currentCell = item.value.cell; // e.g., "C9"
      const sheet = item.value.sheet;

      console.log('🔬 [DEBUG RADIO BUTTON] Cell information:', {
        currentCell: currentCell,
        sheet: sheet,
        sheetRow: sheet.row,
        sheetCol: sheet.col
      });

      // Try to figure out what column J9 would be
      // If C9 is column 2 (0-indexed), then J9 would be column 9
      // Let's try different approaches to access the parameter data
      if (sheet.row !== undefined && sheet.col !== undefined) {
        console.log('🔬 [DEBUG RADIO BUTTON] Parameter cell calculation:', {
          currentRow: sheet.row,
          currentCol: sheet.col,
          parameterColCandidate1: sheet.col + 7, // C+7=J
          parameterColCandidate2: 9, // J is column 9 (0-indexed)
          parameterColCandidate3: 10 // J is column 10 (1-indexed)
        });
      }
    }

    // Also test if the cellValue or outputValue contains parseable image data
    if (item?.value?.cellValue) {
      console.log('🔬 [DEBUG RADIO BUTTON] Testing cellValue parsing:', {
        cellValue: item.value.cellValue,
        isImageUrl: this.isImageUrl(item.value.cellValue),
        hasParentheses: item.value.cellValue.includes('(') && item.value.cellValue.includes(')'),
        hasSemicolons: item.value.cellValue.includes(';'),
        parsedOptions: this.parseCellValueToSelectOptions(item.value.cellValue)
      });
    }

    if (item?.value?.outputValue && typeof item?.value?.outputValue === 'string') {
      console.log('🔬 [DEBUG RADIO BUTTON] Testing outputValue parsing:', {
        outputValue: item.value.outputValue,
        isImageUrl: this.isImageUrl(item.value.outputValue),
        hasParentheses: item.value.outputValue.includes('(') && item.value.outputValue.includes(')'),
        hasSemicolons: item.value.outputValue.includes(';'),
        parsedOptions: this.parseCellValueToSelectOptions(item.value.outputValue)
      });
    }
  }

}

